/**
 * The ticket queue — the README's first link, and the screen that makes Conduit a ticketing
 * product rather than an API that stores tickets.
 *
 * Two decisions worth knowing before changing anything here:
 *
 * 1. **The lifecycle is not reimplemented in this file.** Each ticket arrives with `allowed_next`,
 *    computed server-side from the same table the server enforces. A copy of the rules here would
 *    drift, and the screen would then offer a button that always fails — which teaches people to
 *    distrust the buttons.
 *
 * 2. **Unassigned work is shown, not hidden.** "In progress with nobody assigned" is refused
 *    outright by the schema, but a triaged ticket nobody has picked up is legal and is exactly
 *    what goes stale, so the queue counts it in a stat rather than leaving it to be noticed.
 */

import { useCallback, useState, type JSX, type SyntheticEvent } from 'react';

import { api, type Ticket, type TicketState } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

const STATE_TONE: Record<string, Tone> = {
  new: 'info',
  triage: 'info',
  in_progress: 'warn',
  pending: 'muted',
  resolved: 'ok',
  closed: 'muted',
  canceled: 'muted',
};

/** P1 and P2 read as alarming because they are. */
function priorityTone(priority: string): Tone {
  if (priority === 'p1') return 'danger';
  if (priority === 'p2') return 'warn';
  return 'muted';
}

/** The states that need an account of the work before the server will accept them. */
const NEEDS_RESOLUTION: readonly string[] = ['resolved', 'canceled'];

export function Tickets(): JSX.Element {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  const { data, error, loading } = useAsync(() => api.tickets(100), [revision]);
  const [writeError, setWriteError] = useState<unknown>(null);
  const [me, setMe] = useState<string | null>(null);

  const whoami = useAsync(() => api.whoami(), []);
  if (me === null && whoami.data !== null) setMe(whoami.data.principal_id);

  const tickets = data?.items ?? [];
  const open = tickets.filter((t) => t.state !== 'closed' && t.state !== 'canceled');
  const urgent = open.filter((t) => t.priority === 'p1' || t.priority === 'p2').length;
  const unassigned = open.filter((t) => t.assignee_id === null).length;

  const move = (t: Ticket, to: TicketState): void => {
    setWriteError(null);

    // Asked for here rather than on a separate screen: the resolution is the one thing the server
    // will refuse the move without, so prompting at the moment of the decision is the only place
    // it does not feel like an interruption.
    let resolution: string | null = null;
    if (NEEDS_RESOLUTION.includes(to)) {
      resolution = window.prompt(`What was done? (required to mark this ticket ${to})`);
      if (resolution === null || resolution.trim() === '') return;
    }

    // Starting work needs an owner. Claiming it is the sensible default — the person clicking
    // "in progress" is almost always the person about to do it.
    const assignee = to === 'in_progress' && t.assignee_id === null ? me : null;

    void api
      .moveTicket(t.id, to, resolution, assignee)
      .then(refresh)
      .catch((e: unknown) => {
        setWriteError(e);
      });
  };

  const claim = (t: Ticket): void => {
    setWriteError(null);
    void api
      .assignTicket(t.id, me)
      .then(refresh)
      .catch((e: unknown) => {
        setWriteError(e);
      });
  };

  return (
    <>
      <PageHeader
        title="Tickets"
        subtitle="Work in, work owned, work closed on the record. Every move is appended to the audit ledger."
      />

      <ErrorBox error={error} />
      <ErrorBox error={writeError} />

      <Card>
        <StatRow>
          <Stat label="Open" value={open.length} />
          <Stat
            label="P1 and P2"
            value={urgent}
            tone={urgent > 0 ? 'danger' : 'neutral'}
            hint="service down or badly degraded"
          />
          <Stat
            label="Unassigned"
            value={unassigned}
            tone={unassigned > 0 ? 'warn' : 'neutral'}
            hint="nobody has picked these up"
          />
          <Stat label="Total shown" value={tickets.length} />
        </StatRow>
      </Card>

      <RaiseTicket onRaised={refresh} />

      <Card
        title="Queue"
        subtitle="The moves offered on each row are the moves the server will accept — they come from the lifecycle itself, not from this screen."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Priority', 'Ticket', 'State', 'Owner', 'Resolution', 'Move']}
            empty="No tickets yet. Raise one above."
          >
            {tickets.map((t) => (
              <tr key={t.id}>
                <td>
                  <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>
                </td>
                <td>
                  {/* Rendered as text by React, never as markup. A ticket title is
                      attacker-controlled — anyone who can raise a ticket controls it. */}
                  <div className="table__strong">{t.title}</div>
                  <Formula>{t.kind.replace('_', ' ')}</Formula>
                </td>
                <td>
                  <Badge tone={STATE_TONE[t.state] ?? 'muted'}>{t.state.replace('_', ' ')}</Badge>
                </td>
                <td>
                  {t.assignee_id === null ? (
                    <button
                      type="button"
                      className="button button--small"
                      onClick={() => {
                        claim(t);
                      }}
                    >
                      claim
                    </button>
                  ) : (
                    <Formula>{t.assignee_id === me ? 'you' : t.assignee_id.slice(0, 8)}</Formula>
                  )}
                </td>
                <td>
                  <Formula>{t.resolution ?? '—'}</Formula>
                </td>
                <td>
                  {t.allowed_next.length === 0 ? (
                    <Formula>closed</Formula>
                  ) : (
                    t.allowed_next.map((next) => (
                      <button
                        key={next}
                        type="button"
                        className="button button--small"
                        onClick={() => {
                          move(t, next);
                        }}
                      >
                        {next.replace('_', ' ')}
                      </button>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

/** Raising a ticket. The first write path a ticketing product needs, and it was missing. */
function RaiseTicket({ onRaised }: { onRaised: () => void }): JSX.Element {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('incident');
  const [priority, setPriority] = useState('p3');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    void api
      .createTicket({ kind, priority, title: title.trim(), description: description.trim() })
      .then(() => {
        setTitle('');
        setDescription('');
        onRaised();
      })
      .catch((e: unknown) => {
        setError(e);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Card
      title="Raise a ticket"
      subtitle="It starts in triage-pending. Classification selects the playbook and the SLA, so the lifecycle will not let it be worked before it is classified."
    >
      <ErrorBox error={error} />
      <form className="inline-form" onSubmit={submit}>
        <input
          className="input"
          placeholder="What is wrong?"
          value={title}
          maxLength={1024}
          required
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
        <input
          className="input"
          placeholder="Detail (optional)"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
        <select
          className="input"
          aria-label="Record type"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
          }}
        >
          <option value="incident">Incident</option>
          <option value="service_request">Service request</option>
          <option value="problem">Problem</option>
          <option value="change">Change</option>
          <option value="security_incident">Security incident</option>
        </select>
        <select
          className="input"
          aria-label="Priority"
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value);
          }}
        >
          <option value="p1">P1 — service down</option>
          <option value="p2">P2 — major degradation</option>
          <option value="p3">P3 — moderate</option>
          <option value="p4">P4 — low</option>
        </select>
        <button type="submit" className="button" disabled={busy || title.trim() === ''}>
          {busy ? 'Raising…' : 'Raise'}
        </button>
      </form>
    </Card>
  );
}
